/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserApiToken } from "../../slices/currentUser/currentUserSlice";
import { m11ResearchStudyResourceChanged, selectM11ResearchStudyResource } from "../../slices/m11ReportAuthoringTool/m11ReportAuthoringToolSlice";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import jsonEquals from "../../functions/jsonEquals";
import CodeableConceptEntry from "../../datatypeComponents/CodeableConceptEntry";
import BooleanEntry from "../../datatypeComponents/BooleanEntry";
import StringEntry from "../../datatypeComponents/StringEntry";
import DecimalEntry from "../../datatypeComponents/DecimalEntry";
import CodeEntry from "../../datatypeComponents/CodeEntry";
import { convertValueSetToCodeableConceptSet } from "../../dictionaries/THOandFHIRValueSets";
import saveM11Changes from "../../functions/saveM11ChangesAPI";
import { selectM11ResearchStudyFoi } from "../../slices/m11StepCount/m11StepCountSlice";
import { validateStep } from "../../functions/validateM11ReportAPI";

const saveOverallDesignChanges = async (dispatch, apiToken, m11ResearchStudyFoi, overallDesignState,
    setAlertModalState, setStartingOverallDesignState) => {

    let changesStates = {
        overallDesignState: overallDesignState
    };
    let response = await saveM11Changes(apiToken, 13, m11ResearchStudyFoi, changesStates);
    if (response?.success) {
        if (response.m11ResearchStudyResource) {
            dispatch(m11ResearchStudyResourceChanged(response.m11ResearchStudyResource));
        }
        setStartingOverallDesignState(JSON.parse(JSON.stringify(overallDesignState)));
    } else {
        if (response.error) {
            alert(response.error);
        }
    }
};

const M11Step013EnterOverallDesign = forwardRef(({ theM11ResearchStudyFoi, setAlertModalState }, ref) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyResource = useSelector(selectM11ResearchStudyResource);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [overallDesignState, setOverallDesignState] = useState({
        populationType: "", interventionModel: "", populationDiagnosis: "",
        controlType: "", minimumAge: "", minimumAgeUnits: "",
        maximumAge: "", maximumAgeUnits: "",
        controlDescription: "", siteDistribution: "", geographicScope: "",
        interventionAssignmentMethod: "", randomisationType: "", otherInterventionAssignmentMethod: "",
        stratificationIndicator: "", masterProtocolIndicator: "",
        drugDeviceComboIndicator: "", adaptiveTrialIndicator: ""
    });
    const [startingOverallDesignState, setStartingOverallDesignState] = useState({
        populationType: "", interventionModel: "", populationDiagnosis: "",
        controlType: "", minimumAge: "", minimumAgeUnits: "",
        maximumAge: "", maximumAgeUnits: "",
        controlDescription: "", siteDistribution: "", geographicScope: "",
        interventionAssignmentMethod: "", randomisationType: "", otherInterventionAssignmentMethod: "",
        stratificationIndicator: "", masterProtocolIndicator: "",
        drugDeviceComboIndicator: "", adaptiveTrialIndicator: ""
    });

    useImperativeHandle(ref, () => ({
        unsavedChanges: (m11ResearchStudyResource && !jsonEquals(overallDesignState, startingOverallDesignState)),
        async saveAllChanges() {
            await saveOverallDesignChanges(dispatch, apiToken, m11ResearchStudyFoi, overallDesignState,
                setAlertModalState, setStartingOverallDesignState);
        }
    }));

    useEffect(() => {
        if (m11ResearchStudyResource) {
            let startingData = {
                populationType: "", interventionModel: "", populationDiagnosis: "",
                controlType: "", minimumAge: "", minimumAgeUnits: "",
                maximumAge: "", maximumAgeUnits: "",
                controlDescription: "", siteDistribution: "", geographicScope: "",
                interventionAssignmentMethod: "", randomisationType: "", otherInterventionAssignmentMethod: "",
                stratificationIndicator: "", masterProtocolIndicator: "",
                drugDeviceComboIndicator: "", adaptiveTrialIndicator: ""
            };
            if (m11ResearchStudyResource.classifier?.length > 0) {
                for (const classifier of m11ResearchStudyResource.classifier) {
                    if (classifier.coding?.[0]?.system === "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl") {
                        if (classifier.coding[0].code === "C218503" ||
                            classifier.coding[0].code === "C218504") {
                            startingData.populationType = classifier;
                        } else if (classifier.coding[0].code === "C82640" ||
                            classifier.coding[0].code === "C82639" ||
                            classifier.coding[0].code === "C82637" ||
                            classifier.coding[0].code === "C82638" ||
                            classifier.coding[0].code === "C142568" ||
                            (classifier.coding[0].code === "C17649" && classifier.coding[0].display === "Other Intervention Model (C17649)")) {
                            startingData.interventionModel = classifier;
                        } else if (classifier.coding[0].code === "C49648" ||
                            classifier.coding[0].code === "C49649" ||
                            classifier.coding[0].code === "C120841" ||
                            classifier.coding[0].code === "C218505" ||
                            classifier.coding[0].code === "C218506" ||
                            classifier.coding[0].code === "C184727" ||
                            classifier.coding[0].code === "C28280") {
                            startingData.controlType = classifier;
                        } else if (classifier.coding[0].code === "C217004" ||
                            classifier.coding[0].code === "C217005") {
                            startingData.siteDistribution = classifier;
                        } else if (classifier.coding[0].code === "C217006" ||
                            classifier.coding[0].code === "C217007") {
                            startingData.geographicScope = classifier;
                        } else if (classifier.coding[0].code === "C25196" ||
                            classifier.coding[0].code === "C222801" ||
                            (classifier.coding[0].code === "C17649" && classifier.coding[0].display === "Other Intervention Assignment Method (C17649)")) {
                            startingData.interventionAssignmentMethod = classifier;
                        }
                    } else if (classifier.coding?.[0]?.system === "ForUseWithM11ReportAuthoringTool") {
                        if (classifier.coding[0].display === "Control Description") {
                            startingData.controlDescription = classifier.text;
                        } else if (classifier.coding[0].display === "Randomisation Type") {
                            startingData.randomisationType = classifier.text;
                        } else if (classifier.coding[0].display === "Other Intervention Assignment Method") {
                            startingData.otherInterventionAssignmentMethod = classifier.text;
                        } else if (classifier.coding[0].display === "Stratification Indicator") {
                            if (classifier.text === "True") {
                                startingData.stratificationIndicator = true;
                            } else if (classifier.text === "False") {
                                startingData.stratificationIndicator = false;
                            }
                        } else if (classifier.coding[0].display === "Master Protocol Indicator") {
                            if (classifier.text === "True") {
                                startingData.masterProtocolIndicator = true;
                            } else if (classifier.text === "False") {
                                startingData.masterProtocolIndicator = false;
                            }
                        } else if (classifier.coding[0].display === "Drug/Device Combination Product Indicator") {
                            if (classifier.text === "True") {
                                startingData.drugDeviceComboIndicator = true;
                            } else if (classifier.text === "False") {
                                startingData.drugDeviceComboIndicator = false;
                            }
                        } else if (classifier.coding[0].display === "Adaptive Trial Design Indicator") {
                            if (classifier.text === "True") {
                                startingData.adaptiveTrialIndicator = true;
                            } else if (classifier.text === "False") {
                                startingData.adaptiveTrialIndicator = false;
                            }
                        }
                    }
                }
            }
            if (m11ResearchStudyResource.condition?.length > 0) {
                startingData.populationDiagnosis = m11ResearchStudyResource.condition[0];
            }
            if (m11ResearchStudyResource.extension?.length) {
                for (const extension of m11ResearchStudyResource.extension) {
                    if (extension.url === "http://hl7.org/fhir/StructureDefinition/artifact-useContext" &&
                        extension.valueUsageContext?.code?.code === "age" &&
                        extension.valueUsageContext.valueRange) {
                        let ageRange = extension.valueUsageContext.valueRange;
                        if (ageRange.low) {
                            if (ageRange.low.value || ageRange.low.value === 0) {
                                startingData.minimumAge = ageRange.low.value;
                            }
                            startingData.minimumAgeUnits = ageRange.low.unit || "";
                        }
                        if (ageRange.high) {
                            startingData.maximumAge = ageRange.high.value || "";
                            startingData.maximumAgeUnits = ageRange.high.unit || "";
                        }
                    }
                }
            }
            startingData.loaded = true;
            setOverallDesignState(startingData);
            setStartingOverallDesignState(startingData);
        }
    }, [m11ResearchStudyResource]);

    return <div>{overallDesignState?.loaded &&
        <div>
            <h3>Enter Overall Design Details part 1</h3>
            <h4 style={{ marginBottom: "0px" }}>Select Population Type</h4>
            <CodeableConceptEntry elementName='populationType' fieldLabel='Population Type'
                codeableConceptLevelValueSet={[
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218503",
                            "display": "With Disease (C218503)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218504",
                            "display": "Without Disease (C218504)"
                        }]
                    }
                ]} noWriteIn={true}
                startingValue={overallDesignState.populationType}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Select Intervention Model</h4>
            <CodeableConceptEntry elementName='interventionModel' fieldLabel='Intervention Model'
                codeableConceptLevelValueSet={[
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C82640",
                            "display": "Single Group (C82640)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C82639",
                            "display": "Parallel Group (C82639)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C82637",
                            "display": "Cross-over (C82637)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C82638",
                            "display": "Factorial (C82638)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C142568",
                            "display": "Sequential (C142568)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C17649",
                            "display": "Other Intervention Model (C17649)"
                        }]
                    }
                ]} noWriteIn={true}
                startingValue={overallDesignState.interventionModel}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Enter Population Diagnosis or Condition</h4>
            <CodeableConceptEntry elementName='populationDiagnosis'
                fieldLabel='Population Diagnosis or Condition'
                systemChoices={[{ "uri": "https://www.meddra.org", "display": "MedDRA" }]}
                                startingValue={overallDesignState.populationDiagnosis}
                startCollapsed={overallDesignState.populationDiagnosis}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Select Control Type</h4>
            <CodeableConceptEntry elementName='controlType' fieldLabel='Control Type'
                codeableConceptLevelValueSet={[
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C49648",
                            "display": "Placebo (C49648)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C49649",
                            "display": "Active Comparator (C49649)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C120841",
                            "display": "Dose Response (C120841)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218505",
                            "display": "Different Dose or Regimen (C218505)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218506",
                            "display": "External (C218506)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C184727",
                            "display": "Sham Procedure (C184727)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C28280",
                            "display": "No Control (C28280)"
                        }]
                    }
                ]} noWriteIn={true}
                startingValue={overallDesignState.controlType}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Enter Minimum and Maximum Age with Units of Age in Hours, Days, Weeks, Months, or Years.</h4>
            <DecimalEntry elementName="minimumAge" fieldLabel="Minimum Age"
                startingValue={overallDesignState.minimumAge}
                setResourceState={setOverallDesignState} />
            <CodeEntry elementName='minimumAgeUnits' fieldLabel='Minimum Age Units'
                startingValue={overallDesignState.minimumAgeUnits}
                setResourceState={setOverallDesignState} dataEntryStyle='dropdown'
                allowedValues={["years", "months", "weeks", "days", "hours"]} />
            <DecimalEntry elementName="maximumAge" fieldLabel="Maximum Age"
                startingValue={overallDesignState.maximumAge}
                setResourceState={setOverallDesignState} />
            <CodeEntry elementName='maximumAgeUnits' fieldLabel='Maximum Age Units'
                startingValue={overallDesignState.maximumAgeUnits}
                setResourceState={setOverallDesignState} dataEntryStyle='dropdown'
                allowedValues={["years", "months", "weeks", "days", "hours"]} />
            <h4 style={{ marginBottom: "0px" }}>For the Control Description, enter Nonproprietary Name, or International Nonproprietary Name, or 'Not applicable'.</h4>
            <StringEntry elementName='controlDescription' fieldLabel='Control Description'
                startingValue={overallDesignState.controlDescription}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Select Site Distribution</h4>
            <CodeableConceptEntry elementName='siteDistribution' fieldLabel='Site Distribution'
                codeableConceptLevelValueSet={[
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C217004",
                            "display": "Single-Centre (C217004)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C217005",
                            "display": "Multicentre (C217005)"
                        }]
                    }
                ]} noWriteIn={true}
                startingValue={overallDesignState.siteDistribution}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Select Site Geographic Scope</h4>
            <CodeableConceptEntry elementName='geographicScope' fieldLabel='Site Geographic Scope'
                codeableConceptLevelValueSet={[
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C217006",
                            "display": "Single Country (C217006)"
                        }]
                    },
                    {
                        "coding": [{
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C217007",
                            "display": "Multiple Countries (C217007)"
                        }]
                    }
                ]} noWriteIn={true}
                startingValue={overallDesignState.geographicScope}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Select Intervention Assignment Method</h4>
            <CodeableConceptEntry elementName='interventionAssignmentMethod'
                fieldLabel='Intervention Assignment Method'
                noWriteIn={true}
                codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet([
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C25196",
                        "display": "Randomisation (C25196)"
                    },
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C222801",
                        "display": "No Intervention Assignment Method (C222801)"
                    },
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C17649",
                        "display": "Other Intervention Assignment Method (C17649)"
                    }
                ])}
                startCollapsed={overallDesignState.interventionAssignmentMethod}
                startingValue={overallDesignState.interventionAssignmentMethod}
                setResourceState={setOverallDesignState} />
            {overallDesignState.interventionAssignmentMethod?.coding?.[0]?.code === "C25196" &&
                <div>
                    <p>If Intervention Assignment Method is 'Randomisation', then enter Randomisation Type (e.g., simple randomisation, block randomisation).</p>
                    <StringEntry elementName='randomisationType' fieldLabel='Randomisation Type'
                        startingValue={overallDesignState.randomisationType}
                        setResourceState={setOverallDesignState} />
                </div>}
            {overallDesignState.interventionAssignmentMethod?.coding?.[0]?.code === "C17649" &&
                <div>
                    <p>If Intervention Assignment Method is 'Other', then enter Other Intervention Assignment Method.</p>
                    <StringEntry elementName='otherInterventionAssignmentMethod' fieldLabel='Other Intervention Assignment Method'
                        startingValue={overallDesignState.otherInterventionAssignmentMethod}
                        setResourceState={setOverallDesignState} />
                </div>}
            <h4 style={{ marginBottom: "0px" }}>Select Stratification Indicator as True or False</h4>
            <BooleanEntry elementName='stratificationIndicator' fieldLabel='Stratification Indicator'
                storeFalse={true}
                startingValue={overallDesignState.stratificationIndicator}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Select Master Protocol Indicator as True or False</h4>
            <BooleanEntry elementName='masterProtocolIndicator' fieldLabel='Master Protocol Indicator'
                storeFalse={true}
                startingValue={overallDesignState.masterProtocolIndicator}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Select Drug/Device Combination Product Indicator as True or False</h4>
            <BooleanEntry elementName='drugDeviceComboIndicator' fieldLabel='Drug/Device Combination Product Indicator'
                storeFalse={true}
                startingValue={overallDesignState.drugDeviceComboIndicator}
                setResourceState={setOverallDesignState} />
            <h4 style={{ marginBottom: "0px" }}>Select Adaptive Trial Design Indicator as True or False</h4>
            <BooleanEntry elementName='adaptiveTrialIndicator' fieldLabel='Adaptive Trial Design Indicator'
                storeFalse={true}
                startingValue={overallDesignState.adaptiveTrialIndicator}
                setResourceState={setOverallDesignState} />
        </div >
    }</div>
});

const M11Step013EnterOverallDesignHelperText = <div>
    <h3>Enter Overall Design Details part 1</h3>
    <div style={{ textAlign: "left" }}>
        <p>Enter Population Type with one of: With Disease (C218503); Without Disease (C218504).</p>
        <p>Enter Intervention Model with one of: Single Group (C82640); Parallel Group (C82639); Cross-over (C82637); Factorial (C82638); Sequential (C142568); Other (C17649).</p>
        <p>Enter Population Diagnosis or Condition, using MedDRA Preferred Term(s) or indicate 'other' and describe. You can click '+ Add Coding' to enter the MedDRA code and display directly, or 'Search BioPortal' (if signed in with an API key) and find a term from MedDRA.</p>
        <p>Enter Control Type with one of: Placebo (C49648); Active Comparator (C49649); Dose Response (C120841); Different Dose or Regimen (C218505); External (C218506); Sham Procedure (C184727); No Control (C28280).</p>
        <p>Enter Minimum and Maximum Age with Units of Age in Hours, Days, Weeks, Months, or Years.</p>
        <p>For the Control Description, enter Nonproprietary Name, or International Nonproprietary Name, or 'Not applicable'.</p>
        <p>Enter Site Distribution with one of: Single-Centre (C217004), Multicentre (C217005).</p>
        <p>Enter Site Geographic Scope with one of: Single Country (C217006); Multiple Countries (C217007).</p>
        <p>Enter Intervention Assignment Method with one of: Randomisation (C25196); No Intervention Assignment Method (C222801); Other (C17649).</p>
        <div style={{ marginLeft: "24px" }}>
            <p>If Intervention Assignment Method is 'Randomisation', then enter Randomisation Type (e.g., simple randomisation, block randomisation).</p>
            <p>If Intervention Assignment Method is 'Other', then enter Other Intervention Assignment Method.</p>
        </div>
        <p>Enter Stratification Indicator with one of: True or False.</p>
        <p>Enter Master Protocol Indicator with one of: True or False.</p>
        <p>Enter Drug/Device Combination Product Indicator with one of: True or False.</p>
        <p>Enter Adaptive Trial Design Indicator with one of: True or False.</p>
        <p>Click the 'Save All Changes' button.</p>
    </div>
</div >

const populationTypeValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C218503",
        "display": "With Disease (C218503)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C218504",
        "display": "Without Disease (C218504)"
    }
];

const interventionModelValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C82640",
        "display": "Single Group (C82640)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C82639",
        "display": "Parallel Group (C82639)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C82637",
        "display": "Cross-over (C82637)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C82638",
        "display": "Factorial (C82638)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C142568",
        "display": "Sequential (C142568)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C17649",
        "display": "Other Intervention Model (C17649)"
    }
];

const controlTypeValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C49648",
        "display": "Placebo (C49648)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C49649",
        "display": "Active Comparator (C49649)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C120841",
        "display": "Dose Response (C120841)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C218505",
        "display": "Different Dose or Regimen (C218505)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C218506",
        "display": "External (C218506)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C184727",
        "display": "Sham Procedure (C184727)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C28280",
        "display": "No Control (C28280)"
    }
];

const siteDistributionValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C217004",
        "display": "Single-Centre (C217004)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C217005",
        "display": "Multicentre (C217005)"
    }
];

const geographicScopeValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C217006",
        "display": "Single Country (C217006)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C217007",
        "display": "Multiple Countries (C217007)"
    }
];

const interventionAssignmentMethodValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C25196",
        "display": "Randomisation (C25196)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C222801",
        "display": "No Intervention Assignment Method (C222801)"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C17649",
        "display": "Other Intervention Assignment Method (C17649)"
    }
];

// eslint-disable-next-line no-empty-pattern
const M11Step013EnterOverallDesignValidationCheck = ({ }) => {

  const apiToken = useSelector(selectCurrentUserApiToken);
  const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

  const [validationCheckState, setValidationCheckState] = useState({});

  useEffect(() => {
    if (m11ResearchStudyFoi) {
      validateStep(apiToken, 13, m11ResearchStudyFoi,
        setValidationCheckState)
    }
  }, [m11ResearchStudyFoi]);

  return <div>{validationCheckState?.success && <div>
        <h4>Step 13 (Enter Overall Design Details part 1) {validationCheckState.step13numberOfChecks} issues checked. {validationCheckState.step13numberPassed} passed. {validationCheckState.step13numberFailed} failed.</h4>
        <p>Population Type is present and matches an allowed value: {validationCheckState.resultCheckPopulationType}</p>
        <p>Intervention Model is present and matches an allowed value: {validationCheckState.resultCheckInterventionModel}</p>
        <p>Population Diagnosis or Condition is present: {validationCheckState.resultCheckPopulationDiagnosis}</p>
        <p>Control Type is present and matches an allowed value: {validationCheckState.resultCheckControlType}</p>
        <p>Minimum Age is present and includes a numerical value and allowed unit of measure: {validationCheckState.resultCheckMinimumAge}</p>
        <p>Maximum Age is present and includes a numerical value and allowed unit of measure: {validationCheckState.resultCheckMaximumAge}</p>
        <p>Control Description is present: {validationCheckState.resultCheckControlDescription}</p>
        <p>Site Distribution is present and matches an allowed value: {validationCheckState.resultCheckSiteDistribution}</p>
        <p>Geographic Scope is present and matches an allowed value: {validationCheckState.resultCheckGeographicScope}</p>
        <p>Intervention Assignment Method is present and matches an allowed value: {validationCheckState.resultCheckInterventionAssignmentMethod}</p>
        <p>If Intervention Assignment Method is Randomisation or Other, explanatory text is provided: {validationCheckState.resultCheckInterventionAssignmentMethodExplained}</p>
        <p>Stratification Indicator is present and matches an allowed value: {validationCheckState.resultCheckStratificationIndicator}</p>
        <p>Master Protocol Indicator is present and matches an allowed value: {validationCheckState.resultCheckMasterProtocolIndicator}</p>
        <p>Drug/Device Combination Product Indicator is present and matches an allowed value: {validationCheckState.resultCheckDeviceProductIndicator}</p>
        <p>Adaptive Trial Design Indicator is present and matches an allowed value: {validationCheckState.resultCheckAdaptiveIndicator}</p>
    </div>}  </div>
}

export default M11Step013EnterOverallDesign;
export {
    M11Step013EnterOverallDesignHelperText, M11Step013EnterOverallDesignValidationCheck, populationTypeValueSet,
    interventionModelValueSet, controlTypeValueSet, siteDistributionValueSet, geographicScopeValueSet,
    interventionAssignmentMethodValueSet
};